/*
 * Candles by Midnight — sticky Add-to-Cart bar.
 * Proxies to the real <product-form> submit button so cart/variant logic is never duplicated.
 * Visible only once the main button has scrolled above the viewport, and hidden again while
 * the footer is on screen so it never covers it. Mirrors price, variant, label, disabled and
 * loading state from the real button.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyATC extends HTMLElement {
      connectedCallback() {
        // Fixed positioning breaks inside transformed ancestors, so live directly under <body>.
        // Moving re-runs connectedCallback there, which does the setup.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        document.querySelectorAll(`sticky-atc[data-section-id="${this.dataset.sectionId}"]`).forEach((bar) => {
          if (bar !== this) bar.remove();
        });

        this.submitBtn = document.getElementById(this.dataset.submitId);
        this.mainPrice = document.getElementById(this.dataset.priceId);
        this.trigger = this.querySelector('[data-sticky-atc-trigger]');
        this.label = this.querySelector('[data-sticky-label]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.variantEl = this.querySelector('[data-sticky-variant]');

        if (!this.submitBtn || !this.trigger) return;

        this.pastButton = false;
        this.footerInView = false;

        this.trigger.addEventListener('click', () => {
          if (this.submitBtn.hasAttribute('disabled') || this.submitBtn.classList.contains('loading')) return;
          this.submitBtn.click();
        });

        this.syncButton();
        this.syncPrice();

        if ('IntersectionObserver' in window) {
          this.buttonObserver = new IntersectionObserver(([entry]) => {
            this.pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.update();
          });
          this.buttonObserver.observe(this.submitBtn);

          const footer = document.querySelector('.shopify-section-group-footer-group, footer');
          if (footer) {
            this.footerObserver = new IntersectionObserver(([entry]) => {
              this.footerInView = entry.isIntersecting;
              this.update();
            });
            this.footerObserver.observe(footer);
          }
        }

        this.btnObserver = new MutationObserver(() => this.syncButton());
        this.btnObserver.observe(this.submitBtn, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.mainPrice) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.dataset.sectionId) return;
            const variant = event.data.variant;
            if (this.variantEl) {
              this.variantEl.textContent = variant && variant.title !== 'Default Title' ? variant.title : '';
            }
          });
        }
      }

      update() {
        const show = this.pastButton && !this.footerInView;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.toggleAttribute('inert', !show);
      }

      syncButton() {
        const disabled = this.submitBtn.hasAttribute('disabled');
        this.trigger.disabled = disabled;
        this.trigger.classList.toggle('is-loading', this.submitBtn.classList.contains('loading'));
        const label = this.submitBtn.querySelector('span');
        const text = label && label.textContent.trim();
        if (text && this.label) this.label.textContent = text;
      }

      /*
       * Mirror the FIGURES only — never the price node's markup.
       * `#price-<section_id>` wraps the whole pdp-price-block (offer box, shipping
       * progress and so on); cloning it would turn a one-row bar into a panel.
       */
      syncPrice() {
        if (!this.mainPrice || !this.priceEl) return;

        const now = this.mainPrice.querySelector('.pdp-price__now');
        const was = this.mainPrice.querySelector('.pdp-price__was');

        this.priceEl.textContent = '';

        if (!now) {
          this.priceEl.textContent = (this.mainPrice.textContent || '').trim().split('\n')[0].trim();
          return;
        }

        const nowEl = document.createElement('span');
        nowEl.className = 'sticky-atc__price-now';
        nowEl.textContent = now.textContent.trim();
        this.priceEl.appendChild(nowEl);

        if (was && was.textContent.trim()) {
          const wasEl = document.createElement('s');
          wasEl.className = 'sticky-atc__price-was';
          wasEl.textContent = was.textContent.trim();
          this.priceEl.appendChild(wasEl);
        }
      }

      disconnectedCallback() {
        if (this.isConnected || !this.initialized) return;
        this.initialized = false;
        this.buttonObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.btnObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.variantUnsubscriber?.();
      }
    }
  );
}
